import { detectInAppBrowser } from '../detect-devices/detect-in-app-browser';
import { isIframe } from './is-iframe';
import { sendOpenSearchLogs } from './send-open-search-logs.ts';

// Hand-off for a blocked redirect popup. Always a link the customer taps.

export const HANDOFF_EVENT_GROUP = 'cashier_redirect_handoff';
export const HANDOFF_WATCH_TIMEOUT_MS = 2000;

const X_SAFARI_APPS = ['tiktok', 'twitter'];

export const getPlatform = (ua = window?.navigator?.userAgent || '') => {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  return 'other';
};

export const getOsVersion = (ua = window?.navigator?.userAgent || '') => {
  const android = ua.match(/Android (\d+(?:\.\d+)*)/i);
  if (android) return android[1];
  const ios = ua.match(/OS (\d+(?:_\d+)*) like Mac OS X/i);
  if (ios) return ios[1].replace(/_/g, '.');
  return null;
};

const hostAndPath = (url) => {
  const parsed = new URL(url);
  return `${parsed.host}${parsed.pathname}${parsed.search}`;
};

// {technique, href, target?} or null (invalid url).
// In-app: deep link where one exists; otherwise a plain top-window link.
export const buildHandoffLink = ({ platform, inAppBrowser, url, universalLink }) => {
  if (!url) return null;

  try {
    const topLink = { technique: 'top_link', href: url, target: '_top' };

    // Merchant-app WebViews ignore intents and schemes but follow a top navigation.
    if (!inAppBrowser || inAppBrowser === 'android_webview' || inAppBrowser === 'ios_webview')
      return topLink;

    if (platform === 'android') {
      return {
        technique: 'android_intent',
        href:
          `intent://${hostAndPath(url)}#Intent;scheme=https;action=android.intent.action.VIEW;` +
          `package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`,
      };
    }

    if (platform === 'ios') {
      if (universalLink) return { technique: 'universal_link', href: universalLink };
      if (X_SAFARI_APPS.includes(inAppBrowser)) {
        return { technique: 'x_safari_https', href: `x-safari-https://${hostAndPath(url)}` };
      }
    }

    return topLink;
  } catch (e) {
    return null;
  }
};

export const copyToClipboard = async (text) => {
  try {
    if (window?.navigator?.clipboard?.writeText) {
      await window.navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // some WebViews block the clipboard API
  }

  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'absolute';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return !!ok;
  } catch (e) {
    return false;
  }
};

// Page hidden soon after the tap: the link worked. Still visible: it was ignored.
export const watchHandoffResult = ({ timeoutMs = HANDOFF_WATCH_TIMEOUT_MS, onLeft, onStayed }) => {
  let done = false;
  let timer;

  const finish = (callback) => {
    if (done) return;
    done = true;
    cleanup();
    if (typeof callback === 'function') callback();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') finish(onLeft);
  };
  const onPageHide = () => finish(onLeft);

  const cleanup = () => {
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  timer = setTimeout(() => finish(onStayed), timeoutMs);

  return cleanup;
};

export const getHandoffContext = () => {
  const ua = window?.navigator?.userAgent || '';
  const env = detectInAppBrowser(ua);

  return {
    in_app_browser: env.in_app_browser,
    is_in_app_browser: env.is_in_app_browser,
    platform: getPlatform(ua),
    os_version: getOsVersion(ua),
    is_iframe: isIframe() ? 1 : 0,
    viewport_width: window?.innerWidth,
    viewport_height: window?.innerHeight,
    screen_width: window?.screen?.width,
    screen_height: window?.screen?.height,
    user_agent: ua,
  };
};

export const isInAppBrowser = () => detectInAppBrowser().is_in_app_browser === 1;

const safeHost = (url) => {
  try {
    return new URL(url).host;
  } catch (e) {
    return null;
  }
};

export const logRedirectPopupBlocked = ({ redirect_url, ...details } = {}) => {
  const context = getHandoffContext();
  const link = buildHandoffLink({
    platform: context.platform,
    inAppBrowser: context.in_app_browser,
    url: redirect_url,
  });

  sendOpenSearchLogs({
    event_group: HANDOFF_EVENT_GROUP,
    event_name: 'redirect_popup_blocked',
    event_data: {
      ...context,
      redirect_host: safeHost(redirect_url),
      handoff_technique: link ? link.technique : 'copy_only',
      ...details,
    },
  });
};
